import React from 'react';
import { Analysis } from '../../types';
import './styles.scss';

/**
 * The "whole card" for one analysis: inputs, results metadata and the
 * conversation that produced it. Shared by My Analyses and Community so a
 * shared link shows everything the owner sees (minus owner-only actions).
 */
const AnalysisDetails: React.FC<{ analysis: Analysis }> = ({ analysis }) => {
  const history = analysis.conversation_history || [];
  const metadata = analysis.metadata && typeof analysis.metadata === 'object' ? analysis.metadata : {};
  const parameters = analysis.parameters && typeof analysis.parameters === 'object' ? analysis.parameters : {};

  return (
    <div className="details-tab-content">
      <h3 className="details-heading">Analysis Details</h3>

      <section className="details-block">
        <h4 className="details-block-title">Inputs</h4>
        <table className="details-table">
          <tbody>
            <tr><td>Query</td><td>{analysis.query}</td></tr>
            <tr><td>Analysis Type</td><td>{(analysis.analysis_type || '—').replace(/_/g, ' ')}</td></tr>
            <tr><td>Location</td><td>{analysis.location || '—'}</td></tr>
            <tr><td>Coordinates</td><td>{analysis.location_lat != null ? `${analysis.location_lat}, ${analysis.location_lng}` : '—'}</td></tr>
            <tr><td>Time Period</td><td>{analysis.start_year || '—'} → {analysis.end_year || '—'}</td></tr>
            {Object.keys(parameters).length > 0 && (
              <tr><td>Parameters</td><td><pre>{JSON.stringify(parameters, null, 2)}</pre></td></tr>
            )}
            {analysis.processing_time_seconds != null && (
              <tr><td>Processing Time</td><td>{Number(analysis.processing_time_seconds).toFixed(1)}s</td></tr>
            )}
            {analysis.user_name && (
              <tr><td>Author</td><td>{analysis.user_name}</td></tr>
            )}
            <tr><td>Created</td><td>{new Date(analysis.created_at).toLocaleString()}</td></tr>
            {analysis.is_shared && analysis.shared_at && (
              <tr><td>Shared</td><td>{new Date(analysis.shared_at).toLocaleString()}</td></tr>
            )}
          </tbody>
        </table>
      </section>

      {Object.keys(metadata).length > 0 && (
        <section className="details-block">
          <h4 className="details-block-title details-block-title-results">Results / Metadata</h4>
          <table className="details-table">
            <tbody>
              {Object.entries(metadata).map(([key, value]) => (
                <tr key={key}>
                  <td className="details-key">{key.replace(/_/g, ' ')}</td>
                  <td>
                    {value !== null && typeof value === 'object'
                      ? <pre className="details-pre-scroll">{JSON.stringify(value, null, 2)}</pre>
                      : String(value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {history.length > 0 && (
        <section className="details-block">
          <h4 className="details-block-title details-block-title-chat">Conversation</h4>
          <div className="details-chat">
            {history.map((msg, i) => (
              <div key={i} className={`details-chat-msg ${msg.role === 'user' ? 'details-chat-user' : 'details-chat-assistant'}`}>
                <div className="details-chat-role">{msg.role === 'user' ? 'You' : 'LLEO'}</div>
                <div className="details-chat-text">{msg.content}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default AnalysisDetails;
